import { Component, Input, OnChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as moment from 'moment';

import { RosterInviteFirstBill } from '../../../../../../models/scheduled-event';
import { ScheduledEventService } from '../../../../../../services/scheduled-event/scheduled-event.service';
import { dateStringFromISO8601, ordinalSuffixedDay } from '../../../../../../utils';
import { getCurrencySymbolForCurrency } from '../../../../../../reporting-core/currency';
import { CurrencyField } from '../../../../../../models/organization-config-enums';
import { getMembershipIntervalDisplay } from '../../../../../../models/membership';

enum LoadState {
  idle = 'idle',
  calculating = 'calculating',
  loaded = 'loaded',
  failed = 'failed',
}

/**
 * Shows the pro-rated first bill for a roster invite, under the Start Date
 * field of both "invite to roster" modals (PRO-1458). Self-contained: renders
 * nothing gate-worthy, so the parent's submit logic never has to know about it.
 */
@Component({
  selector: 'app-roster-invite-first-bill',
  templateUrl: './roster-invite-first-bill.component.html',
  styleUrls: ['./roster-invite-first-bill.component.css']
})
export class RosterInviteFirstBillComponent implements OnChanges {
  @Input() rosterId: number | null = null;
  @Input() startDate = '';

  public readonly LoadState = LoadState;
  public state: LoadState = LoadState.idle;
  public result: RosterInviteFirstBill | null = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly ordinalSuffixedDay = ordinalSuffixedDay;

  private changedSubject = new Subject<null>();

  // Only the most recently issued request may render, in case a later change
  // (roster swap, date edit) resolves before an earlier one.
  private requestCount = 0;

  private loadedRosterId: number | null = null;

  // Subscribed here rather than in ngOnInit: ngOnChanges runs first, and an
  // input set before init would otherwise emit into nothing.
  constructor(
    private scheduledEventService: ScheduledEventService
  ) {
    this.changedSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.load();
    });
  }

  ngOnChanges() {
    if (!this.rosterId) {
      // Invalidate any response still in flight for the roster just cleared.
      this.requestCount++;
      this.state = LoadState.idle;
      this.result = null;
      this.loadedRosterId = null;
      return;
    }

    // A plan with no fixed bill day answers the same for every date, so a
    // date edit alone need not ask again.
    if ((this.rosterId === this.loadedRosterId) &&
        (this.state === LoadState.loaded) &&
        this.result && !this.result.prorates) {
      return;
    }

    this.state = LoadState.calculating;
    this.changedSubject.next();
  }

  /**
   * The day before period_end_date, as an ISO date string -- rendered as the
   * last day this pro-rated bill actually covers.
   */
  public periodEndDayBefore(): string {
    return moment(this.result.period_end_date).subtract(1, 'day').format('YYYY-MM-DD');
  }

  public currencySymbol(): string {
    return getCurrencySymbolForCurrency(this.result?.currency as CurrencyField);
  }

  public intervalDisplayLower(): string {
    return getMembershipIntervalDisplay(this.result.interval).toLowerCase();
  }

  private load() {
    const rosterId = this.rosterId;
    if (!rosterId) { return; }

    this.requestCount++;
    const thisRequestCount = this.requestCount;

    this.scheduledEventService
      .getRosterInviteFirstBill(rosterId, this.startDate || null)
      .then(result => {
        if (thisRequestCount !== this.requestCount) { return; }
        this.result = result;
        this.loadedRosterId = rosterId;
        this.state = LoadState.loaded;
      })
      .catch(() => {
        if (thisRequestCount !== this.requestCount) { return; }
        this.result = null;
        this.state = LoadState.failed;
      });
  }
}
